import type { Vec2, Vec3 } from '../../geo/types';
import type { Frame, MeshBuilder, VertexStyle } from '../mesh/meshBuilder';
import { orientedFace, planPoint, subFrame } from './solids';

/** Ridge along u between u0 and u1; eaves at v0 and v1. */
export interface GableSpec {
  readonly u0: number;
  readonly u1: number;
  readonly v0: number;
  readonly v1: number;
  readonly eaveY: number;
  readonly ridgeY: number;
  readonly ends: { readonly start: boolean; readonly end: boolean };
}

export interface HipSpec {
  readonly u0: number;
  readonly u1: number;
  readonly v0: number;
  readonly v1: number;
  readonly eaveY: number;
  readonly pitch: number;
}

/**
 * Single slope from the outer eave up to a higher wall at vInner. The strip of wall
 * under the high edge is emitted too, so walls to eaveY plus this roof form a closed solid.
 */
export interface LeanToSpec {
  readonly u0: number;
  readonly u1: number;
  readonly vOuter: number;
  readonly vInner: number;
  readonly eaveY: number;
  readonly topY: number;
}

export interface RoofStyles {
  readonly roof: VertexStyle;
  readonly wall: VertexStyle;
}

const RIDGE_EPSILON = 1e-3;

function slopeUv(u0: number, eaveV: number, eaveY: number): (p: Vec3) => Vec2 {
  return (p) => [p[0] - u0, Math.hypot(-p[2] - eaveV, p[1] - eaveY)];
}

function endUv(uEave: number, v0: number, eaveY: number): (p: Vec3) => Vec2 {
  return (p) => [-p[2] - v0, Math.hypot(p[0] - uEave, p[1] - eaveY)];
}

function wallUv(p: Vec3): Vec2 {
  return [-p[2], p[1]];
}

export function gableRoof(
  builder: MeshBuilder,
  styles: RoofStyles,
  frame: Frame,
  spec: GableSpec
): void {
  const { u0, u1, v0, v1, eaveY, ridgeY } = spec;
  const vc = (v0 + v1) / 2;
  const hi = Math.sign(v1 - v0);
  const ridgeA = planPoint(u0, ridgeY, vc);
  const ridgeB = planPoint(u1, ridgeY, vc);
  orientedFace(
    builder,
    styles.roof,
    frame,
    [planPoint(u0, eaveY, v1), planPoint(u1, eaveY, v1), ridgeB, ridgeA],
    [0, 1, -hi],
    slopeUv(u0, v1, eaveY)
  );
  orientedFace(
    builder,
    styles.roof,
    frame,
    [planPoint(u0, eaveY, v0), planPoint(u1, eaveY, v0), ridgeB, ridgeA],
    [0, 1, hi],
    slopeUv(u0, v0, eaveY)
  );
  if (spec.ends.start) {
    const tri = [planPoint(u0, eaveY, v0), planPoint(u0, eaveY, v1), ridgeA];
    orientedFace(builder, styles.wall, frame, tri, [-1, 0, 0], wallUv);
  }
  if (spec.ends.end) {
    const tri = [planPoint(u1, eaveY, v0), planPoint(u1, eaveY, v1), ridgeB];
    orientedFace(builder, styles.wall, frame, tri, [1, 0, 0], wallUv);
  }
}

function hipAlongU(builder: MeshBuilder, style: VertexStyle, frame: Frame, spec: HipSpec): number {
  const { u0, u1, v0, v1, eaveY } = spec;
  const half = (v1 - v0) / 2;
  const vc = v0 + half;
  const ridgeY = eaveY + half * Math.tan(spec.pitch);
  const a = planPoint(u0 + half, ridgeY, vc);
  const b = planPoint(u1 - half, ridgeY, vc);
  const c00 = planPoint(u0, eaveY, v0);
  const c10 = planPoint(u1, eaveY, v0);
  const c11 = planPoint(u1, eaveY, v1);
  const c01 = planPoint(u0, eaveY, v1);
  const hasRidge = u1 - u0 - 2 * half > RIDGE_EPSILON;
  const left = hasRidge ? [c01, c11, b, a] : [c01, c11, a];
  const right = hasRidge ? [c00, c10, b, a] : [c00, c10, a];
  orientedFace(builder, style, frame, left, [0, 1, -1], slopeUv(u0, v1, eaveY));
  orientedFace(builder, style, frame, right, [0, 1, 1], slopeUv(u0, v0, eaveY));
  orientedFace(builder, style, frame, [c00, c01, a], [-1, 1, 0], endUv(u0, v0, eaveY));
  orientedFace(builder, style, frame, [c10, c11, b], [1, 1, 0], endUv(u1, v0, eaveY));
  return ridgeY;
}

/** Four-sided hip roof over an axis-aligned rectangle; returns the ridge height. */
export function hipRoof(
  builder: MeshBuilder,
  style: VertexStyle,
  frame: Frame,
  spec: HipSpec
): number {
  const length = spec.u1 - spec.u0;
  const width = spec.v1 - spec.v0;
  if (length >= width) return hipAlongU(builder, style, frame, spec);
  const rotated = subFrame(frame, (spec.u0 + spec.u1) / 2, (spec.v0 + spec.v1) / 2, Math.PI / 2);
  return hipAlongU(builder, style, rotated, {
    ...spec,
    u0: -width / 2,
    u1: width / 2,
    v0: -length / 2,
    v1: length / 2,
  });
}

export function leanToRoof(
  builder: MeshBuilder,
  styles: RoofStyles,
  frame: Frame,
  spec: LeanToSpec
): void {
  const { u0, u1, vOuter, vInner, eaveY, topY } = spec;
  const out = Math.sign(vOuter - vInner);
  orientedFace(
    builder,
    styles.roof,
    frame,
    [
      planPoint(u0, eaveY, vOuter),
      planPoint(u1, eaveY, vOuter),
      planPoint(u1, topY, vInner),
      planPoint(u0, topY, vInner),
    ],
    [0, 1, -out],
    slopeUv(u0, vOuter, eaveY)
  );
  const inner = [
    planPoint(u0, eaveY, vInner),
    planPoint(u1, eaveY, vInner),
    planPoint(u1, topY, vInner),
    planPoint(u0, topY, vInner),
  ];
  orientedFace(builder, styles.wall, frame, inner, [0, 0, out], wallUv);
  for (const [u, dir] of [
    [u0, -1],
    [u1, 1],
  ] as const) {
    const tri = [planPoint(u, eaveY, vOuter), planPoint(u, eaveY, vInner), planPoint(u, topY, vInner)];
    orientedFace(builder, styles.wall, frame, tri, [dir, 0, 0], wallUv);
  }
}
